import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import actionCreator from "../../../redux/action/actionCreator";
import Toast from "../../../components/MessageToast";
import MetricFormDialog from "../../components/forms/MetricsForm";
import { CREATE_METRIC, UPDATE_METRIC } from "../../../redux/metrics/types";
import { toast } from "react-toastify";
import {
  DISPLAY_ORDER_MIN,
  METRIC_CATEGORY,
  getMetricCategory,
  getMetricCategoryLabel,
} from "../../components/metric/metricOptions";

// Rendered as a Dialog directly on the metrics list - not a routed page.
// `metric` (when editing) is the item object the list already has loaded, so
// no extra fetch-by-id round trip is needed before the form can open.
export default ({ open, metric, category: categoryProp, defaultDisplayOrder, onClose, onSaved }) => {
  const metricId = metric?.id;
  const category = getMetricCategory(metric?.category || categoryProp);
  const dispatch = useDispatch();

  const operationStore = useSelector((store) =>
    metricId ? store.updateMetricGlobalStore : store.createMetricGlobalStore
  );
  const [state, setState] = useState({ openToast: false });

  const openToast = ({ message, type, onClose: onToastClose }) =>
    Toast({ message, type, duration: 3000, onClose: onToastClose });

  const handleFormSubmit = async (formData) => {
    const metricCategory = getMetricCategory(formData.category);
    const displayOrder = Number(formData.displayOrder);
    const missingFields = [];

    if (!Number.isInteger(displayOrder) || displayOrder < DISPLAY_ORDER_MIN) {
      missingFields.push("Display order");
    }

    if (metricCategory === METRIC_CATEGORY.REACH) {
      if (!formData.title?.trim()) {
        missingFields.push("Title");
      }
      if (!formData.value?.trim()) {
        missingFields.push("Value");
      }
    }

    if (metricCategory === METRIC_CATEGORY.IMPACT) {
      if (!formData.text?.trim()) {
        missingFields.push("Text");
      }
      if (!formData.icon) {
        missingFields.push("Icon");
      }
    }

    if (missingFields.length > 0) {
      return openToast({
        message: `Please Enter All required fields \nMissing: ${missingFields.join(
          ", "
        )}`,
        type: "warning",
      });
    }

    const metricInfo =
      metricCategory === METRIC_CATEGORY.REACH
        ? {
            category: metricCategory,
            title: formData.title.trim(),
            value: formData.value.trim(),
            displayOrder,
          }
        : {
            category: metricCategory,
            text: formData.text.trim(),
            icon: formData.icon,
            displayOrder,
          };

    await toast.promise(
      dispatch(
        actionCreator({
          method: metricId ? "PATCH" : "POST",
          path: metricId ? `/api/metrics/${metricId}` : "/api/metrics",
          actionType: metricId ? UPDATE_METRIC : CREATE_METRIC,
          data: metricInfo,
        })
      ),
      {
        pending: `${!metricId ? "Creating" : "Updating"} metric`,
      }
    );

    setState({ ...state, openToast: true });
  };

  if (operationStore.message && state.openToast) {
    Toast({
      message: operationStore.message,
      type: operationStore.message.includes("successful") ? "success" : "error",
      onClose: () => {
        if (operationStore.message.includes("successful")) {
          onSaved();
        }
      },
    });

    setState({ ...state, openToast: false });
  }

  const metricCategoryLabel = getMetricCategoryLabel(category);

  return (
    <MetricFormDialog
      open={open}
      onClose={onClose}
      isCreate={!metricId}
      title={`${metricId ? "Edit" : "Add"} ${metricCategoryLabel} Metric`}
      category={category}
      onSubmit={handleFormSubmit}
      baseContentData={metric || {}}
      defaultDisplayOrder={defaultDisplayOrder}
      isLoading={operationStore.loading}
    />
  );
};
